import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Divider,
  Radio,
  Select,
  Switch,
  Typography,
} from "antd";
import { CheckCircleOutlined, GlobalOutlined } from "@ant-design/icons";
import useTimePreferences from "../../hooks/useTimePreferences";
import {
  getEffectiveTimezone,
  saveTimePreferences,
} from "../../utils/timePreferences";
import {
  getBrowserTimezone,
  getSupportedTimezones,
  getTimezoneOffsetLabel,
} from "../../utils/timezoneUtils";
import "./time.css";
import "./TimeDateSettings.css";

const { Title, Text } = Typography;

const TimeDateSettings = () => {
  const savedPreferences = useTimePreferences();
  const [preferences, setPreferences] = useState(savedPreferences);
  const timezones = useMemo(getSupportedTimezones, []);

  useEffect(() => setPreferences(savedPreferences), [savedPreferences]);

  const effectiveTimezone = getEffectiveTimezone(preferences);
  const isDirty =
    JSON.stringify(preferences) !== JSON.stringify(savedPreferences);
  const timezoneOptions = useMemo(
    () =>
      timezones.map((timezone) => ({
        value: timezone,
        label: `${timezone}  (${getTimezoneOffsetLabel(timezone)})`,
      })),
    [timezones],
  );

  const updatePreference = (key, value) =>
    setPreferences((current) => ({ ...current, [key]: value }));

  const selectTimeSource = (timeSource) => {
    setPreferences((current) => {
      if (timeSource === "manual") {
        return {
          ...current,
          timeSource,
          autoDetectTimezone: false,
          selectedTimezone: "",
          manualOffset: current.manualOffset || {
            sign: "+",
            hours: 0,
            minutes: 0,
          },
        };
      }
      if (timeSource === "timezone") {
        return {
          ...current,
          timeSource,
          autoDetectTimezone: false,
          manualOffset: null,
        };
      }
      return {
        ...current,
        timeSource: "browser",
        autoDetectTimezone: true,
        selectedTimezone: "",
        manualOffset: null,
      };
    });
  };

  const updateManualOffset = (key, value) =>
    setPreferences((current) => ({
      ...current,
      manualOffset: { ...current.manualOffset, [key]: value },
    }));

  const save = () => saveTimePreferences(preferences);

  return (
    <main className="time-date-settings">
      <Breadcrumb
        items={[
          { title: "Settings" },
          { title: "Preferences" },
          { title: "Time & Date" },
        ]}
      />
      <div className="time-date-settings__heading">
        <div>
          <Title level={2}>Time & Date</Title>
          <Text type="secondary">
            Configure the personal time reference shown in the application
            header.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={save}
          disabled={!isDirty}
        >
          Save preferences
        </Button>
      </div>

      <section
        className="time-date-settings__content"
        aria-label="Time preferences"
      >
        <Card className="time-date-settings__card" bordered={false}>
          <div className="time-date-settings__section-heading">
            <div>
              <h3>UTC Time</h3>
              <p>UTC is the operational reference and is always visible.</p>
            </div>
            <span className="time-date-settings__fixed-status">Enabled</span>
          </div>

          <Divider />

          <div className="time-date-settings__section-heading">
            <div>
              <h3>Local Time</h3>
              <p>
                Show a secondary local or vessel-operating timezone in the
                header.
              </p>
            </div>
            <Switch
              checked={preferences.showLocalTime}
              onChange={(checked) => updatePreference("showLocalTime", checked)}
              checkedChildren="ON"
              unCheckedChildren="OFF"
              aria-label="Display local time"
            />
          </div>

          <div className="time-date-settings__field">
            <label>Time source</label>
            <Radio.Group
              value={preferences.timeSource}
              onChange={(event) => selectTimeSource(event.target.value)}
            >
              <Radio value="browser">Browser Timezone</Radio>
              <Radio value="timezone">Preferred Timezone</Radio>
              <Radio value="manual">Manual UTC Offset</Radio>
            </Radio.Group>
          </div>

          {preferences.timeSource === "timezone" && (
            <div className="time-date-settings__field">
              <label htmlFor="preferred-timezone">Preferred timezone</label>
              <Select
                id="preferred-timezone"
                showSearch
                optionFilterProp="label"
                value={preferences.selectedTimezone || undefined}
                placeholder="Select timezone"
                options={timezoneOptions}
                disabled={preferences.timeSource !== "timezone"}
                onChange={(value) => {
                  updatePreference("selectedTimezone", value);
                  selectTimeSource("timezone");
                }}
                aria-label="Preferred timezone"
              />
              <span className="time-date-settings__help">
                {preferences.timeSource === "browser"
                  ? `Using browser timezone: ${getBrowserTimezone() || "not available"}`
                  : preferences.selectedTimezone
                    ? `${preferences.selectedTimezone} · ${getTimezoneOffsetLabel(preferences.selectedTimezone)}`
                    : "Choose a timezone to configure Local Time."}
              </span>
            </div>
          )}

          {preferences.timeSource === "manual" && (
            <div className="time-date-settings__field">
              <label>UTC Offset</label>
              <div className="time-date-settings__offset-inputs">
                <span className="time-date-settings__offset-prefix">UTC</span>
                <Select
                  value={preferences.manualOffset?.sign || "+"}
                  options={[
                    { value: "+", label: "+" },
                    { value: "-", label: "-" },
                  ]}
                  disabled={preferences.timeSource !== "manual"}
                  onChange={(value) => updateManualOffset("sign", value)}
                  aria-label="UTC offset sign"
                />
                <Select
                  value={preferences.manualOffset?.hours ?? 0}
                  options={Array.from({ length: 15 }, (_, hours) => ({
                    value: hours,
                    label: String(hours).padStart(2, "0"),
                  }))}
                  disabled={preferences.timeSource !== "manual"}
                  onChange={(value) => updateManualOffset("hours", value)}
                  aria-label="UTC offset hours"
                />
                <Select
                  value={preferences.manualOffset?.minutes ?? 0}
                  options={[0, 30, 45].map((minutes) => ({
                    value: minutes,
                    label: String(minutes).padStart(2, "0"),
                  }))}
                  disabled={preferences.timeSource !== "manual"}
                  onChange={(value) => updateManualOffset("minutes", value)}
                  aria-label="UTC offset minutes"
                />
              </div>
              <span className="time-date-settings__help">
                {preferences.timeSource === "manual"
                  ? `Local Time: ${getTimezoneOffsetLabel(preferences.manualOffset)}`
                  : "Choose Manual UTC Offset to configure a vessel-operating time reference."}
              </span>
            </div>
          )}
        </Card>

        <Card className="time-date-settings__card" bordered={false}>
          <div className="time-date-settings__section-heading">
            <div>
              <h3>Formatting</h3>
              <p>24-hour time is the default maritime presentation.</p>
            </div>
          </div>

          <div className="time-date-settings__field">
            <label>Time format</label>
            <Radio.Group
              value={preferences.timeFormat}
              onChange={(event) =>
                updatePreference("timeFormat", event.target.value)
              }
            >
              <Radio value="24">24 Hour</Radio>
              <Radio value="12">12 Hour</Radio>
            </Radio.Group>
            <span className="time-date-settings__help">
              UTC always remains in 24-hour format.
            </span>
          </div>

          <div className="time-date-settings__field">
            <label htmlFor="date-format">Date format</label>
            <Select
              id="date-format"
              value={preferences.dateFormat}
              onChange={(value) => updatePreference("dateFormat", value)}
              options={[
                { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
                { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
                { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
              ]}
              aria-label="Date format"
            />
          </div>
        </Card>

        <Alert
          type="info"
          showIcon
          icon={<GlobalOutlined />}
          message={
            preferences.showLocalTime && !effectiveTimezone
              ? "Local Time is enabled but no timezone is configured. The header will show ‘Not configured’."
              : "Changes apply immediately across open MarineBytes pages after saving."
          }
        />
      </section>
    </main>
  );
};

export default TimeDateSettings;
